import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { debounce, uniqBy } from 'es-toolkit';
import { memo, useRef, useState, useEffect, useCallback } from 'react';
import isEqual from 'react-fast-compare';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { usePrevious } from '@/hooks/usePrevious';
import { type DiscoverLinkRefinement, generateDiscoverLinks } from '@/redux/modules/discoverItems';
import { CategoryLink } from '@/types/CategoryLink';
import { ItemFacet, ItemFacets } from '@/types/ItemFacets';
import { getCategoryBrowsePath, getCategoryDimensionBrowsePath } from '@/utils/browsePath/itemFacetBrowsePath';
import { getCategoryLevel } from '@/utils/categoryGrabber';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getSimilarItemsUrl } from '@/utils/urls';

type Props = {
    categoryLinks?: CategoryLink[];
    className?: string;
    facets?: ItemFacets;
    isInHeader?: boolean;
    suggestions?: any[];
    width?: number;
};

const DiscoveryTagsComponent = ({
    categoryLinks = [],
    className = '',
    facets,
    isInHeader = false,
    suggestions = [],
    width,
}: Props) => {
    const formatMessage = useAppFormatMessage();
    let containerRef = useRef<HTMLDivElement>(null);
    let scrollerRef = useRef<HTMLDivElement>(null);

    const [containerWidth, setContainerWidth] = useState(1000);
    const [left, setLeft] = useState(0);
    const [scrollerWidth, setScrollerWidth] = useState(1000);

    const defaultTags = [
        {
            key: 'navigation.art',
            link: '/c/art/',
            title: formatMessage(['navigation', 'art']),
        },
        {
            key: 'navigation.jewelry',
            link: '/c/jewelry/',
            title: formatMessage(['navigation', 'jewelry']),
        },
        {
            key: 'navigation.asian_antiques',
            link: '/c/asian/',
            title: formatMessage(['navigation', 'asian_antiques']),
        },
        {
            key: 'navigation.furniture',
            link: '/c/furniture/',
            title: formatMessage(['navigation', 'furniture']),
        },
        {
            key: 'navigation.collectibles',
            link: '/c/collectibles/',
            title: formatMessage(['navigation', 'collectibles']),
        },
        {
            key: 'navigation.coins',
            link: '/c/coins-currency-and-stamps/',
            title: formatMessage(['navigation', 'coins']),
        },
        {
            key: 'navigation.home_and_decor',
            link: '/c/home-and-decor/',
            title: formatMessage(['navigation', 'home_and_decor']),
        },
        {
            key: 'navigation.fashion',
            link: '/c/fashion/',
            title: formatMessage(['navigation', 'fashion']),
        },
        {
            key: 'navigation.explore_categories',
            link: '/c/',
            title: formatMessage(['navigation', 'explore_categories']),
        },
    ];

    const handleScrollFunc = () => {
        if (scrollerRef) {
            setLeft(scrollerRef?.current?.scrollLeft);
        }
    };

    const handleScroll = debounce(handleScrollFunc, 10);

    // Implements scroll and ui logic on every render
    useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.addEventListener('scroll', handleScroll);
            handleScroll();
            updateContainerWidth();
        }
    });

    const updateContainerWidth = useCallback(() => {
        let newContainerWidth = containerWidth;
        let newScrollerWidth = scrollerWidth;
        if (containerRef.current) {
            newContainerWidth = containerRef.current.getBoundingClientRect()?.width;
        }

        if (scrollerRef.current) {
            newScrollerWidth = getScrollerWidth();
        }
        setContainerWidth(newContainerWidth);
        setScrollerWidth(newScrollerWidth);
    }, [containerWidth, scrollerWidth]);

    const previousWidth = usePrevious(width);
    const previousFacets = usePrevious(facets);
    const previousSuggestions = usePrevious(suggestions);

    useEffect(() => {
        if (previousWidth !== width || !isEqual(previousFacets, facets) || !isEqual(previousSuggestions, suggestions)) {
            updateContainerWidth();
        }
    }, [facets, previousFacets, previousSuggestions, previousWidth, suggestions, updateContainerWidth, width]);

    useEffect(() => {
        const scrollerRefCopy = scrollerRef.current;
        return () => {
            if (scrollerRefCopy) {
                scrollerRefCopy.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);

    const getScrollerWidth = () => {
        return (
            Array.from(scrollerRef.current.children).reduce((total, card: HTMLAnchorElement) => {
                return total + card.offsetWidth;
            }, 0) +
            (scrollerRef.current.children.length - 1) * 8
        ); // this last bit is for the margins
    };

    const mapLinks = ({ key, link, title }: CategoryLink) => {
        if (!key || !title) {
            return null;
        }

        return (
            <BodySecondary key={key}>
                <div
                    className={cn(
                        '!mr-[15px] !inline-flex !h-[36px] !items-center !rounded-xs !bg-surface-100 !px-xs !tracking-[0.5px] !text-text-secondary !no-underline [box-shadow:0_0_2px_rgba(0,0,0,0.3)] first:!ml-px last:!mr-[4px]',
                        'hover:!bg-border-secondary hover:!text-primary-400 hover:!no-underline',
                        'focus:!bg-border-secondary focus:!text-primary-400 focus:!no-underline',
                        isInHeader && '!shadow-none !m-0 !bg-transparent !p-0'
                    )}
                >
                    <RouteLink
                        className="flex h-full w-full items-center justify-center"
                        to={link}
                    >
                        {title}
                    </RouteLink>
                </div>
            </BodySecondary>
        );
    };

    // copied and refactored from DiscoverItems.js
    const createCategoryLandingLink = useCallback((mainItem: ItemFacet, refinement?: DiscoverLinkRefinement) => {
        const mainCategoryLevel = getCategoryLevel(mainItem);
        const secondCategoryLevel = getCategoryLevel(refinement?.item);
        // `categoryUrl` no longer builds the link, but as elsewhere its presence is what marks the
        // level as a real, linkable category; the name alone is also needed to build the slug.
        const refinedBy =
            refinement && secondCategoryLevel.categoryName && secondCategoryLevel.categoryUrl
                ? { dimension: refinement.dimension, level: secondCategoryLevel }
                : null;

        const key = refinedBy
            ? `${mainCategoryLevel.categoryLabel}-${refinedBy.level.categoryLabel}`
            : `${mainCategoryLevel.categoryLabel}`;

        const link = refinedBy
            ? getCategoryDimensionBrowsePath(
                  mainCategoryLevel.categoryName,
                  refinedBy.dimension,
                  refinedBy.level.categoryName
              )
            : getCategoryBrowsePath(mainCategoryLevel.categoryName);

        const title = refinedBy
            ? `${refinedBy.level.categoryLabel} ${mainCategoryLevel.categoryLabel}`
            : `${mainCategoryLevel.categoryLabel}`;

        return { key, link, mainCategoryLevel, secondCategoryLevel, title };
    }, []);

    const mapSuggestions = ({ label: title, value }: any, index: number) => {
        const link = getSimilarItemsUrl(value);
        return { key: `${index}-${value}`, link, title };
    };

    const suggestionLinks = suggestions.map(mapSuggestions);
    const facetLinks = generateDiscoverLinks(facets, createCategoryLandingLink);

    let linkObjects = [...suggestionLinks, ...categoryLinks, ...facetLinks];
    if (!linkObjects.length) {
        linkObjects = defaultTags;
    }

    const tags = uniqBy(linkObjects, (item) => item.link);
    return (
        <div
            className={cn('relative z-[1] mt-sm mb-[21px] h-[38px] overflow-hidden p-0', className)}
            ref={containerRef}
        >
            <div
                className="!m-0 !inline-block !h-[55px] !w-full overflow-x-auto !overflow-y-hidden !pt-px !whitespace-nowrap xsMax:!mt-[7px] [&::-webkit-scrollbar]:!hidden"
                ref={scrollerRef}
            >
                {tags.map(mapLinks)}
            </div>
            <div
                className={cn(
                    'absolute top-0 bottom-0 left-0 hidden w-[40px] bg-[linear-gradient(270deg,rgba(255,255,255,0)_0%,white_100%)]',
                    left > 5 && 'block'
                )}
            />
            <div
                className={cn(
                    'absolute top-0 right-0 bottom-0 hidden w-[40px] bg-[linear-gradient(90deg,rgba(255,255,255,0)_0%,white_100%)]',
                    left + containerWidth + 5 < scrollerWidth && 'block'
                )}
            />
        </div>
    );
};

export const DiscoveryTags = memo<Props>(DiscoveryTagsComponent, isEqual);
